import { ReactNode, useEffect } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { LoginArea } from '@/components/auth/LoginArea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageCircle, Zap } from 'lucide-react';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { user, isLoading } = useCurrentUser();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-blue-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-blue-900">
        <div className="max-w-md mx-auto px-6 text-center">
          {/* Logo */}
          <div className="mb-8">
            <div className="w-24 h-24 bg-blue-500 rounded-full mx-auto flex items-center justify-center mb-4 shadow-lg">
              <MessageCircle className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
              MensaG
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Mensajería privada con Nostr
            </p>
          </div>

          {/* Login Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-center">Bienvenido a MensaG</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Para comenzar a chatear de forma privada, conéctate con tu cuenta Nostr.
              </p>
              <div className="flex items-center space-x-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-white">Mensajes Privados</p>
                  <p className="text-sm text-gray-500">Cifrado end-to-end con NIP-17</p>
                </div>
              </div>
              <div className="flex items-center space-x-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-800 rounded-full flex items-center justify-center">
                  <Zap className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-white">Pagos Integrados</p>
                  <p className="text-sm text-gray-500">Lightning Network con NIP-57</p>
                </div>
              </div>
              <LoginArea className="w-full" />
            </CardContent>
          </Card>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Al continuar, aceptas usar MensaG para comunicaciones cifradas
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}